import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderIcon, PlusIcon } from '@heroicons/react/24/outline';
import { diaryService } from '../../services/diary';

const FolderSelector = ({ selectedFolder, onChange, className = '' }) => {
  const [folders, setFolders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const folderList = await diaryService.getFolders();
        setFolders(folderList);
      } catch (error) {
        console.error('Error fetching folders:', error);
      }
    };
    fetchFolders();
  }, []);

  const handleFolderSelect = (folder) => {
    onChange(folder);
    setIsOpen(false);
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim() && !folders.includes(newFolderName.trim())) {
      const newFolder = newFolderName.trim();
      setFolders([...folders, newFolder]);
      onChange(newFolder);
      setNewFolderName('');
      setShowNewFolderInput(false);
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Folder
      </label>
      
      {/* Selected Folder Display */}
      <motion.button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full input-field flex items-center justify-between"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center space-x-3">
          <FolderIcon className="h-5 w-5 text-gray-400" />
          <span className={selectedFolder ? 'text-gray-700' : 'text-gray-500'}>
            {selectedFolder || 'Select a folder'}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          ▼
        </motion.div>
      </motion.button>

      {/* Folder Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto"
          >
            {/* No Folder Option */}
            <motion.button
              type="button"
              onClick={() => handleFolderSelect('')}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 ${
                !selectedFolder ? 'bg-primary-50 text-primary-700' : ''
              }`}
              whileHover={{ backgroundColor: '#f9fafb' }}
            >
              <div className="w-5 h-5" />
              <span>No folder</span>
            </motion.button>

            {/* Existing Folders */}
            {folders.map((folder, index) => (
              <motion.button
                key={folder}
                type="button"
                onClick={() => handleFolderSelect(folder)}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors ${
                  selectedFolder === folder ? 'bg-primary-50 text-primary-700' : ''
                }`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ backgroundColor: '#f9fafb' }}
              >
                <FolderIcon className="h-5 w-5 text-primary-500" />
                <span>{folder}</span>
              </motion.button>
            ))}

            {/* Create New Folder */}
            <div className="border-t border-gray-200">
              {!showNewFolderInput ? (
                <motion.button
                  type="button"
                  onClick={() => setShowNewFolderInput(true)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 text-primary-600"
                  whileHover={{ backgroundColor: '#f9fafb' }}
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Create new folder</span>
                </motion.button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="p-4 space-y-3"
                >
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
                    placeholder="Enter folder name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <motion.button
                      type="button"
                      onClick={handleCreateFolder}
                      className="flex-1 bg-primary-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-primary-700 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Create
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setShowNewFolderInput(false);
                        setNewFolderName('');
                      }}
                      className="flex-1 bg-gray-200 text-gray-700 py-2 px-3 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FolderSelector;
